import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ArrowLeft, Phone, MapPin, Check } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import FloatingActions from "./FloatingActions";
import CookieBanner from "./CookieBanner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { services, contactInfo } from "../mock";
import { locations, locationBySlug } from "../locations";

const ServiceIcon = ({ name, className }) => {
  const Icon = Icons[name] || Icons.Sparkles;
  return <Icon className={className} strokeWidth={2.1} />;
};

const LocationDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const loc = locationBySlug(slug);

  const faqs = loc
    ? [
        {
          q: `Bieten Sie Gebäudereinigung in ${loc.name} an?`,
          a: `Ja, wir sind regelmäßig in ${loc.name} und Umgebung im Einsatz – mit dem kompletten Leistungsspektrum von der Unterhaltsreinigung bis zur Glas- und Grundreinigung.`,
        },
        {
          q: `Was kostet eine Gebäudereinigung in ${loc.name}?`,
          a: "Die Kosten richten sich nach Objektgröße, Leistungsumfang und Reinigungsintervall. Nach einer kostenlosen Besichtigung erhalten Sie ein transparentes Festpreis- oder Stundenangebot.",
        },
        {
          q: `Reinigen Sie in ${loc.name} auch außerhalb der Geschäftszeiten?`,
          a: "Ja, auf Wunsch reinigen wir früh morgens, abends oder am Wochenende, damit Ihr Betriebsablauf nicht gestört wird.",
        },
      ]
    : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    if (loc) {
      document.title = loc.metaTitle;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) desc.setAttribute("content", loc.metaDescription);
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", `https://www.mag-facilityservices.de/gebaeudereinigung/${slug}`);
    }

    const scripts = [];
    const addLd = (obj, marker) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute(marker, "true");
      s.text = JSON.stringify(obj);
      document.head.appendChild(s);
      scripts.push(s);
    };

    if (loc) {
      const url = `https://www.mag-facilityservices.de/gebaeudereinigung/${slug}`;
      addLd(
        {
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Gebäudereinigung",
          name: `Gebäudereinigung ${loc.name}`,
          description: loc.metaDescription,
          url,
          areaServed: { "@type": "City", name: loc.name },
          provider: {
            "@type": "ProfessionalService",
            name: "MAG Facility Services UG",
            telephone: "+49 155 62974055",
            address: {
              "@type": "PostalAddress",
              addressLocality: "Augsburg",
              postalCode: "86154",
              addressCountry: "DE",
            },
          },
        },
        "data-location-ld"
      );
      addLd(
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Startseite", item: "https://www.mag-facilityservices.de/" },
            { "@type": "ListItem", position: 2, name: "Einsatzgebiete", item: "https://www.mag-facilityservices.de/#kontakt" },
            { "@type": "ListItem", position: 3, name: `Gebäudereinigung ${loc.name}`, item: url },
          ],
        },
        "data-location-breadcrumb"
      );
      addLd(
        {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
        "data-location-faq"
      );
    }

    return () => {
      document.title = "MAG Facility Services UG | Gebäudereinigung Augsburg";
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.setAttribute("href", "https://www.mag-facilityservices.de/");
      scripts.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc, slug]);

  useEffect(() => {
    if (!loc) navigate("/");
  }, [loc, navigate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [slug]);

  if (!loc) return null;

  const topServices = services.slice(0, 6);
  const otherLocations = locations.filter((l) => l.slug !== slug);

  return (
    <>
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-[74px] bg-white overflow-hidden">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-40 right-0 w-[720px] h-[720px] rounded-full bg-blue-100/60 blur-3xl" />
            <div className="absolute top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-50 blur-3xl" />
          </div>
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
            <Link to="/" className="inline-flex items-center gap-2 text-[14px] font-medium text-slate-600 hover:text-blue-600 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Zur Startseite
            </Link>
            <div className="mt-8 flex items-center gap-4">
              <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600 text-white shrink-0">
                <MapPin className="w-7 h-7" />
              </div>
              <span className="text-[13px] font-bold tracking-[0.18em] text-blue-600 uppercase">
                Einsatzgebiet {loc.name}
              </span>
            </div>
            <h1 className="font-display mt-5 text-slate-900 text-[34px] sm:text-[50px] leading-[1.05] tracking-tight" style={{ fontWeight: 800 }}>
              Gebäudereinigung {loc.name}
            </h1>
            <p className="mt-4 text-[17px] text-slate-600 max-w-2xl leading-relaxed">
              Professionelle Reinigung für Büros, Praxen, Gewerbe & Wohnanlagen in {loc.name} – zuverlässig, versichert und termintreu.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href="/#kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 text-white text-[15px] font-semibold hover:bg-blue-700 transition-colors">
                Kostenloses Angebot anfordern <ArrowRight className="w-4 h-4" />
              </a>
              <a href={contactInfo.phoneHref} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-[15px] font-semibold hover:border-slate-400 hover:bg-slate-50 transition-colors">
                <Phone className="w-4 h-4" /> {contactInfo.phone}
              </a>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-24 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-5">
              <h2 className="font-display text-[26px] text-slate-900" style={{ fontWeight: 800 }}>
                Ihre Gebäudereinigung in {loc.name}
              </h2>
              {loc.intro.map((p, i) => (
                <p key={i} className="text-[16px] leading-relaxed text-slate-600">
                  {p}
                </p>
              ))}

              {/* Leistungen in Ort */}
              <div className="reveal pt-4">
                <h3 className="font-display text-[19px] text-slate-900 mb-4" style={{ fontWeight: 700 }}>
                  Unsere Leistungen in {loc.name}
                </h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {topServices.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/leistungen/${s.slug}`}
                      data-testid={`location-service-${s.slug}`}
                      className="group flex items-center gap-3 rounded-xl border border-slate-200 p-3.5 hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
                    >
                      <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors shrink-0">
                        <ServiceIcon name={s.icon} className="w-5 h-5" />
                      </span>
                      <span className="text-[14px] font-semibold text-slate-800 group-hover:text-blue-700">
                        {s.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="pt-2">
                <ul className="space-y-3">
                  {["Feste Ansprechperson & kurze Wege", "Versichert, zertifiziert & termintreu", "Flexible Zeiten – auch außerhalb der Geschäftszeiten"].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[15px] text-slate-700">
                      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-100 text-blue-600 shrink-0 mt-0.5">
                        <Check className="w-3 h-3" strokeWidth={3} />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar CTA */}
            <aside className="lg:col-span-1">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 lg:sticky lg:top-24">
                <h3 className="font-display text-[19px] text-slate-900" style={{ fontWeight: 700 }}>
                  Angebot für {loc.name}
                </h3>
                <p className="mt-2 text-[14px] text-slate-600 leading-relaxed">
                  Wir melden uns innerhalb von 24 Stunden mit einem individuellen, transparenten Angebot – kostenlos & unverbindlich.
                </p>
                <a href="/#kontakt" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors">
                  Angebot anfordern <ArrowRight className="w-4 h-4" />
                </a>
                <a href={contactInfo.phoneHref} className="mt-3 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-semibold hover:bg-white transition-colors">
                  <Phone className="w-4 h-4" /> Anrufen
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 lg:py-20 bg-white border-t border-slate-100" data-testid="location-faq-section">
          <div className="reveal max-w-3xl mx-auto px-5 sm:px-8">
            <span className="text-[13px] font-bold tracking-[0.18em] text-blue-600 uppercase">Häufige Fragen</span>
            <h2 className="font-display mt-3 text-slate-900 text-[26px] sm:text-[32px]" style={{ fontWeight: 800 }}>
              FAQ zur Gebäudereinigung in {loc.name}
            </h2>
            <Accordion type="single" collapsible className="mt-8">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-slate-200" data-testid={`location-faq-item-${i}`}>
                  <AccordionTrigger className="text-[16px] font-semibold text-slate-900 hover:no-underline py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-slate-600 pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Weitere Standorte */}
        <section className="py-16 bg-slate-50">
          <div className="reveal max-w-5xl mx-auto px-5 sm:px-8">
            <h2 className="font-display text-[24px] text-slate-900 mb-8" style={{ fontWeight: 800 }}>
              Weitere Einsatzgebiete
            </h2>
            <div className="flex flex-wrap gap-3">
              {otherLocations.map((l) => (
                <Link
                  key={l.slug}
                  to={`/gebaeudereinigung/${l.slug}`}
                  data-testid={`other-location-${l.slug}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 px-4 py-2 text-[14px] font-medium text-slate-700 hover:border-blue-300 hover:text-blue-700 transition-colors"
                >
                  <MapPin className="w-4 h-4 text-blue-600" /> {l.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingActions />
      <CookieBanner />
    </>
  );
};

export default LocationDetail;
